import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { dailyStandupAPI, taskAPI, workspaceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { TaskDetailFullModal } from './TaskDetailFullModal';
import {
    CheckCircle2,
    Clock,
    Calendar,
    Loader2,
    ClipboardList,
    ArrowRight,
    Plus,
    Search,
    AlertTriangle,
} from 'lucide-react';
import { priorityConfig, statusColors, formatDate } from '../lib/utils';
import { toast } from 'sonner';

export const DailyStandupPopup = ({ workspaceId, workspaceName, companyName, onDismiss }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [hasPlan, setHasPlan] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [continueOngoing, setContinueOngoing] = useState(false);

    const dismissedRef = useRef(false);
    const openRef = useRef(false);
    openRef.current = open;

    // Plan picker state
    const [candidateTasks, setCandidateTasks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [planning, setPlanning] = useState(false);

    // Filters for planned today
    const [planFilterPriority, setPlanFilterPriority] = useState('all');
    const [planFilterDueDate, setPlanFilterDueDate] = useState('all');

    // Task detail view
    const [detailTask, setDetailTask] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [workspaceMembers, setWorkspaceMembers] = useState([]);

    const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta' }).format(new Date());

    const fetchSummary = useCallback(async () => {
        if (!workspaceId) return;
        const summaryRes = await dailyStandupAPI.getMySummary(workspaceId);
        setSummary(summaryRes.data);
        setHasPlan((summaryRes.data.planned_today || []).length > 0);
        return summaryRes.data;
    }, [workspaceId]);

    const fetchCandidates = useCallback(async (summaryData) => {
        if (!workspaceId || !user?.id) return;
        const plannedIds = new Set((summaryData?.planned_today || []).map(t => t.id));
        const tasksRes = await taskAPI.getByWorkspace(workspaceId, {
            assignee_id: user.id,
            exclude_done: true,
        });
        const allMyTasks = tasksRes.data || [];
        // Candidate picker: only Not Started tasks (Active are auto-planned, Done/Review excluded)
        setCandidateTasks(allMyTasks.filter(t =>
            !plannedIds.has(t.id)
            && t.category !== 'DAILY_TASK'
            && (t.status_group === 'Not Started' || !t.status_group)
        ));
    }, [workspaceId, user?.id]);

    useEffect(() => {
        if (!workspaceId) return;

        let cancelled = false;

        const checkAndFetch = async () => {
            if (dismissedRef.current) return;
            try {
                const statusRes = await dailyStandupAPI.getPopupStatus(workspaceId);
                if (cancelled) return;

                setHasPlan(statusRes.data.has_plan ?? false);

                if (!statusRes.data.should_show) {
                    setLoading(false);
                    setOpen(false);
                    return;
                }

                // Fetch workspace members for TaskDetailFullModal (assignee display)
                workspaceAPI.getMembers(workspaceId).then(res => {
                    if (!cancelled) setWorkspaceMembers(res.data || []);
                }).catch(() => {});

                const summaryData = await fetchSummary();
                if (cancelled) return;

                await fetchCandidates(summaryData);
                setLoading(false);
                setOpen(true);
            } catch (err) {
                console.error('Error fetching daily standup popup:', err);
                setLoading(false);
            }
        };

        checkAndFetch();

        const handleFocus = () => {
            if (!openRef.current && !dismissedRef.current) checkAndFetch();
        };
        window.addEventListener('focus', handleFocus);

        return () => {
            cancelled = true;
            window.removeEventListener('focus', handleFocus);
        };
    }, [workspaceId, fetchSummary, fetchCandidates]);

    const refreshAfterPlanChange = async () => {
        const summaryData = await fetchSummary();
        await fetchCandidates(summaryData);
        // Fire-and-forget snapshot update
        dailyStandupAPI.updatePlan(
            workspaceId,
            todayStr,
            (summaryData?.planned_today || []).map(t => t.id)
        ).catch(() => {});
    };

    const handleAddPlan = async (task) => {
        setPlanning(true);
        try {
            await taskAPI.update(task.id, { standup_plan_date: todayStr });
            toast.success(`"${task.title}" dijadwalkan untuk hari ini`);
        } catch (err) {
            console.error('Error scheduling task:', err);
            toast.error('Gagal menjadwalkan task');
            return;
        } finally {
            setPlanning(false);
        }
        await refreshAfterPlanChange();
    };

    const handleRemovePlan = async (task) => {
        setPlanning(true);
        try {
            await taskAPI.update(task.id, { standup_plan_date: "" });
            toast.success(`"${task.title}" dihapus dari rencana hari ini`);
        } catch (err) {
            console.error('Error removing plan:', err);
            toast.error('Gagal menghapus rencana');
            return;
        } finally {
            setPlanning(false);
        }
        await refreshAfterPlanChange();
    };

    const handleViewDetail = async (task) => {
        setDetailLoading(true);
        try {
            const res = await taskAPI.get(task.id);
            setDetailTask(res.data);
        } catch (err) {
            console.error('Error fetching task detail:', err);
            // Fallback to summary data
            setDetailTask(task);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleDismiss = async () => {
        if (!hasPlan && !continueOngoing && candidateTasks.length > 0) {
            toast.error('Anda belum memiliki rencana task untuk hari ini. Pilih task atau centang "Lanjutkan ongoing task".');
            return;
        }

        if (!summary) {
            dismissedRef.current = true;
            setOpen(false);
            onDismiss?.();
            return;
        }

        setDismissing(true);
        try {
            await dailyStandupAPI.dismiss(workspaceId, summary.date, continueOngoing);
            dismissedRef.current = true;
            setOpen(false);
            onDismiss?.();
        } catch (err) {
            console.error('Error dismissing popup:', err);
            const detail = err.response?.data?.detail;
            toast.error(detail || 'Gagal menutup popup');
        } finally {
            setDismissing(false);
        }
    };

    // --- Task item renderer ---
    const renderTaskItem = (task, { selectable = false, selected = false, clickable = false } = {}) => {
        const priority = priorityConfig[task.priority] || priorityConfig.none;
        const statusColor = statusColors[task.status] || '#9FB0AC';

        return (
            <div
                key={task.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 min-w-0 overflow-hidden ${clickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''}`}
                onClick={clickable ? () => handleViewDetail(task) : undefined}
            >
                <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: statusColor }}
                />
                <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 min-w-0">
                        {task.task_code && (
                            <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                                {task.task_code}
                            </span>
                        )}
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 break-words line-clamp-2">
                            {task.title}
                        </span>
                    </div>
                    {task.parent_task_title && (
                        <div className="text-xs text-gray-400 truncate mt-0.5">
                            ↳ {task.parent_task_code ? `${task.parent_task_code} · ` : ''}{task.parent_task_title}
                        </div>
                    )}
                </div>
                <span
                    className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{
                        backgroundColor: `${priority.color}20`,
                        color: priority.color,
                    }}
                >
                    {priority.label}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1 min-w-[70px] justify-end">
                    <Calendar className="w-3 h-3" />
                    {task.due_date ? formatDate(task.due_date) : '-'}
                </span>
                {selectable && (
                    <Button
                        size="sm"
                        variant={selected ? undefined : "outline"}
                        className={`h-7 text-xs flex-shrink-0 ${selected ? 'bg-green-500 hover:bg-green-600 text-white border-transparent' : ''}`}
                        onClick={(e) => { e.stopPropagation(); (selected ? handleRemovePlan : handleAddPlan)(task); }}
                        disabled={planning}
                    >
                        {selected
                            ? <CheckCircle2 className="w-3 h-3 mr-1" />
                            : <Plus className="w-3 h-3 mr-1" />}
                        Pilih
                    </Button>
                )}
            </div>
        );
    };

    const renderSection = (Icon, title, tasks, color, emptyMessage, clickable = false) => {
        return (
            <div>
                <div className="flex items-center gap-2 mb-2 mt-4">
                    <Icon className="w-4 h-4" style={{ color }} />
                    <span className="text-sm font-semibold" style={{ color }}>
                        {title}
                    </span>
                    <span className="text-xs text-gray-400">({tasks.length})</span>
                </div>
                {tasks.length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-2 px-3">
                        {emptyMessage}
                    </p>
                ) : (
                    <div className="space-y-1">
                        {tasks.map(t => renderTaskItem(t, { clickable }))}
                    </div>
                )}
            </div>
        );
    };

    if (loading || !open) return null;

    const displayName = companyName
        ? `${companyName} / ${summary?.workspace_name || workspaceName || ''}`
        : summary?.workspace_name || workspaceName || '';

    const priorityRank = { urgent: 5, high: 4, medium: 3, low: 2, none: 1 };
    const plannedIds = new Set((summary?.planned_today || []).map(t => t.id));

    const matchesFilters = (t) => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            if (!(t.title.toLowerCase().includes(term)
                || (t.task_code && t.task_code.toLowerCase().includes(term)))) return false;
        }
        if (planFilterPriority !== 'all' && (t.priority || 'none') !== planFilterPriority) return false;
        if (planFilterDueDate !== 'all') {
            if (planFilterDueDate === 'nodue') return !t.due_date;
            if (!t.due_date) return false;
            if (planFilterDueDate === 'overdue' && t.due_date >= todayStr) return false;
            if (planFilterDueDate === 'upcoming' && t.due_date <= todayStr) return false;
        }
        return true;
    };

    const combinedPlanList = [
        ...(summary?.planned_today || []).map(t => ({ task: t, selected: true })),
        ...candidateTasks.filter(t => !plannedIds.has(t.id)).map(t => ({ task: t, selected: false })),
    ]
        .filter(({ task }) => matchesFilters(task))
        .sort((a, b) => {
            const pr = (priorityRank[b.task.priority || 'none'] || 1)
                     - (priorityRank[a.task.priority || 'none'] || 1);
            if (pr !== 0) return pr;
            // Due date terkecil dulu; task tanpa due date di akhir
            const ad = a.task.due_date || '';
            const bd = b.task.due_date || '';
            if (!ad && !bd) return 0;
            if (!ad) return 1;
            if (!bd) return -1;
            return ad.localeCompare(bd);
        });

    const anyFilterActive = searchTerm || planFilterPriority !== 'all' || planFilterDueDate !== 'all';

    return (
        <>
        <Dialog open={open} onOpenChange={(v) => { if (!v && (hasPlan || continueOngoing)) handleDismiss(); }}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden break-words"
                onPointerDownOutside={(e) => { if (!hasPlan && !continueOngoing) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (!hasPlan && !continueOngoing) e.preventDefault(); }}
                onInteractOutside={(e) => { if (!hasPlan && !continueOngoing) e.preventDefault(); }}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-brand-accent" />
                        Daily Standup
                    </DialogTitle>
                </DialogHeader>

                {/* Greeting */}
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Terima kasih untuk supportnya kepada{' '}
                    <strong>{displayName}</strong>. Berikut ringkasan task Anda hari ini
                    ({summary?.date}).
                </p>

                {/* Section 1: Done Yesterday (Review) */}
                {renderSection(
                    CheckCircle2,
                    'Task yang telah Anda selesaikan kemarin (Review)',
                    summary?.done_yesterday || [],
                    '#F5C26B',
                    'Tidak ada task yang berpindah ke Review kemarin',
                    true
                )}

                {/* Section 2: In Progress */}
                {renderSection(
                    Clock,
                    'Task yang sedang Anda kerjakan. Semoga dimudahkan',
                    summary?.in_progress || [],
                    '#6EC1E4',
                    'Tidak ada task yang sedang dikerjakan',
                    true
                )}

                {/* Section 3: Planned Today — unified selectable list */}
                <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                            Rencana hari ini
                        </span>
                        <span className="text-xs text-gray-400">({summary?.planned_today?.length || 0})</span>
                    </div>

                    {/* Checkbox: Lanjutkan ongoing task */}
                    {(summary?.in_progress || []).length > 0 && (
                        <label className="flex items-center gap-2 px-3 py-1.5 mb-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={continueOngoing}
                                onChange={(e) => setContinueOngoing(e.target.checked)}
                                className="w-4 h-4 rounded accent-brand-accent"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                Lanjutkan ongoing task ({summary.in_progress.length})
                            </span>
                        </label>
                    )}

                    {/* Search + Filters */}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="relative flex-1">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Cari task..."
                                className="h-8 pl-8 text-xs"
                            />
                        </div>
                        <Select value={planFilterPriority} onValueChange={setPlanFilterPriority}>
                            <SelectTrigger className="h-8 w-[110px] text-xs">
                                <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Priority</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="none">None</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={planFilterDueDate} onValueChange={setPlanFilterDueDate}>
                            <SelectTrigger className="h-8 w-[120px] text-xs">
                                <SelectValue placeholder="Due Date" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Due Date</SelectItem>
                                <SelectItem value="overdue">Overdue</SelectItem>
                                <SelectItem value="nodue">No Due Date</SelectItem>
                                <SelectItem value="upcoming">Upcoming</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Unified list: selected + unselected */}
                    {combinedPlanList.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2 px-3">
                            {anyFilterActive
                                ? 'Tidak ada task yang cocok dengan filter'
                                : 'Tidak ada task tersedia. Semua task sudah dijadwalkan atau sudah Done.'}
                        </p>
                    ) : (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {combinedPlanList.map(({ task, selected }) =>
                                renderTaskItem(task, { selectable: true, selected, clickable: true })
                            )}
                        </div>
                    )}
                </div>

                {/* Warning if no plan */}
                {!hasPlan && !continueOngoing && candidateTasks.length > 0 && (
                    <div className="flex items-center gap-2 p-3 mt-2 rounded-lg bg-yellow-50 border border-yellow-200">
                        <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                        <p className="text-xs text-yellow-700">
                            Anda belum memiliki rencana task untuk hari ini. Pilih task di "Rencana hari ini" sebelum Submit Plan.
                        </p>
                    </div>
                )}
                {!hasPlan && candidateTasks.length === 0 && (
                    <div className="flex items-center gap-2 p-3 mt-2 rounded-lg bg-green-50 border border-green-200">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <p className="text-xs text-green-700">
                            Semua task sudah selesai. Tidak ada task yang perlu direncanakan untuk hari ini.
                        </p>
                    </div>
                )}

                <DialogFooter className="mt-4">
                    <Button
                        onClick={handleDismiss}
                        disabled={dismissing || planning || (!hasPlan && !continueOngoing && candidateTasks.length > 0)}
                        className="w-full"
                    >
                        {dismissing ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <ArrowRight className="w-4 h-4 mr-2" />
                        )}
                        Submit Plan
                    </Button>
                </DialogFooter>
            </DialogContent>
            </Dialog>

            {/* Task Detail Modal — renders above Daily Standup popup */}
            {detailLoading && (
                <Dialog open={true}>
                    <DialogContent className="max-w-[95vw] w-full h-[90vh] p-0" hideCloseButton>
                        <DialogTitle className="hidden"></DialogTitle>
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                        </div>
                    </DialogContent>
                </Dialog>
            )}
            {detailTask && (
                <TaskDetailFullModal
                    isOpen={!!detailTask}
                    onClose={() => setDetailTask(null)}
                    task={detailTask}
                    listId={detailTask.list_id}
                    statuses={[]}
                    members={workspaceMembers}
                    readOnly={true}
                />
            )}
        </>
    );
};